import { useCallback, useMemo } from 'react';

import { CONSENT_TYPE_DCPD_DECLINE } from '../constants';
import { useCoverageConsentConfirmation } from './useCoverageConsentConfirmation';

interface UseDcpdDeclineConfirmationOptions {
  quoteId: number;
  vehicleVin: string;
  vehicleId: number;
  currentDcpdCode: string | undefined;
  isOtherDeductible: (code: string, field: 'dcpd_deductible_cad') => boolean;
  onApplyChange: (newCode: string, clearOther: boolean) => void;
}

export const useDcpdDeclineConfirmation = ({
  quoteId,
  vehicleVin,
  vehicleId,
  currentDcpdCode,
  isOtherDeductible,
  onApplyChange,
}: UseDcpdDeclineConfirmationOptions) => {
  const shouldClearOther = useCallback(
    (newCode: string) => !isOtherDeductible(newCode, 'dcpd_deductible_cad'),
    [isOtherDeductible],
  );

  const {
    requestConfirmation,
    applyChangeDirectly,
    resetState,
    modalProps: baseModalProps,
  } = useCoverageConsentConfirmation({
    quoteId,
    vehicleVin,
    vehicleId,
    consentType: CONSENT_TYPE_DCPD_DECLINE,
    onApplyChange,
    shouldClearOther,
  });

  const handleDcpdChange = useCallback(
    (newCode: string) => {
      const hadValue = !!currentDcpdCode;
      const isClearing = !newCode;

      if (hadValue && isClearing) {
        requestConfirmation(newCode, currentDcpdCode || '');
      } else {
        applyChangeDirectly(newCode);
      }
    },
    [currentDcpdCode, requestConfirmation, applyChangeDirectly],
  );

  const modalProps = useMemo(
    () => ({
      isOpen: baseModalProps.isOpen,
      onClose: baseModalProps.onClose,
      onConfirm: baseModalProps.onConfirm,
      isLoading: baseModalProps.isLoading,
    }),
    [baseModalProps],
  );

  return {
    handleDcpdChange,
    resetState,
    modalProps,
  };
};
